import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createCycleDelivery,
  updateCycleDelivery,
  confirmDeliveryReceived,
  createCycleAlignerSets,
  updateCycleAlignerSet,
  type CreateDeliveryPayload,
  type UpdateDeliveryPayload,
  type CreateAlignerSetsPayload,
  type UpdateAlignerSetPayload,
} from '../../../api/treatment-ops';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidateTreatmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['patientDeliveryAddresses', patientId] }),
  ]);
}

export function useCreateCycleDelivery(patientId: string, treatmentId: string, cycleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateDeliveryPayload) =>
      createCycleDelivery(patientId, treatmentId, cycleId, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useUpdateCycleDelivery(
  patientId: string,
  treatmentId: string,
  cycleId: string,
  deliveryId: string,
) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateDeliveryPayload) =>
      updateCycleDelivery(patientId, treatmentId, cycleId, deliveryId, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useConfirmDeliveryReceived(patientId: string, treatmentId: string, cycleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => confirmDeliveryReceived(patientId, treatmentId, cycleId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useCreateCycleAlignerSets(patientId: string, treatmentId: string, cycleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAlignerSetsPayload) =>
      createCycleAlignerSets(patientId, treatmentId, cycleId, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export type UpdateAlignerSetArgs = {
  readonly aligner_set_id: string;
  readonly patch: UpdateAlignerSetPayload;
};

export function useUpdateAlignerSet(patientId: string, treatmentId: string, cycleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: UpdateAlignerSetArgs) =>
      updateCycleAlignerSet(
        patientId,
        treatmentId,
        cycleId,
        args.aligner_set_id,
        args.patch,
        getToken(),
      ),
    // Aligner-set PATCH does not affect delivery addresses; skip that key.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
      ]),
  });
}
